import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, ChevronDown, ChefHat, Clock, Lightbulb, RefreshCw } from "lucide-react";

export function MealTable({ mc, meals, activeOptions, setActiveOptions, collapsedMeals, setCollapsedMeals, getItems, mealMacros, getMealName, getFoodName, getFoodDesc, updateFoodWeight, removeFoodFromMeal, setTargetMeal, setShowAddFood, recipes, showRecipes, setShowRecipes, loadingRecipes, handleSuggestRecipes, L }) {
  const optIdx = activeOptions[mc.id] || 0;
  const items = getItems(mc.id);
  const totals = mealMacros[mc.id] || { calories: 0, protein: 0, carbs: 0, fat: 0 };
  const collapsed = collapsedMeals[mc.id];

  return (
    <div className={`mb-4 rounded-xl border ${mc.border} overflow-hidden`} data-testid={`meal-${mc.id}`}>
      <div onClick={() => setCollapsedMeals(prev => ({ ...prev, [mc.id]: !prev[mc.id] }))}
        className={`w-full flex items-center justify-between px-4 py-2.5 ${mc.bg} cursor-pointer hover:brightness-95`}
        data-testid={`meal-toggle-${mc.id}`}>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-bold ${mc.text}`}>{getMealName(mc)}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${mc.text} ${mc.bg} border ${mc.border} font-medium`}>{items.length}</span>
          <span className="text-[10px] text-slate-400">{totals.calories.toFixed(0)} kcal</span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]"
            onClick={(e) => { e.stopPropagation(); setTargetMeal(mc.id); setShowAddFood(true); }}
            data-testid={`add-food-${mc.id}`}>
            <Plus className="w-3 h-3 mr-1" /> {L.addBtn}
          </Button>
          <div className={`transition-transform duration-200 ${collapsed ? '-rotate-90' : 'rotate-0'}`}>
            <ChevronDown className={`w-4 h-4 ${mc.text}`} />
          </div>
        </div>
      </div>

      {!collapsed && (
        <div className="bg-white" data-testid={`meal-content-${mc.id}`}>
          {/* Option tabs */}
          <div className="flex border-b border-slate-100">
            {L.opt.map((label, idx) => {
              const optItems = (meals[mc.id] || [])[idx] || [];
              return (
                <button key={`opt-${mc.id}-${idx}`} onClick={() => setActiveOptions(prev => ({ ...prev, [mc.id]: idx }))}
                  className={`flex-1 py-2 text-xs font-semibold text-center transition-all ${
                    optIdx === idx ? `${mc.text} border-b-2 ${mc.border} ${mc.bg}` : 'text-slate-400 hover:text-slate-600'
                  }`}
                  data-testid={`option-${mc.id}-${idx}`}>
                  {label} {optItems.length > 0 && <span className="ml-1 text-[9px] opacity-60">({optItems.length})</span>}
                </button>
              );
            })}
          </div>

          {items.length > 0 ? (
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">{L.food}</th>
                  <th className="text-center px-2 py-2 font-semibold text-slate-600 w-20">{L.weight}</th>
                  <th className="text-center px-2 py-2 font-semibold text-slate-600 w-16">{L.calories}</th>
                  <th className="text-center px-2 py-2 font-semibold text-blue-600 w-16">{L.protein}</th>
                  <th className="text-center px-2 py-2 font-semibold text-amber-600 w-16">{L.carbs}</th>
                  <th className="text-center px-2 py-2 font-semibold text-rose-600 w-16">{L.fat}</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const ss = parseFloat(item.serving_size) || 100;
                  const f = (parseFloat(item.weight) || 0) / ss;
                  const desc = getFoodDesc(item);
                  return (
                    <tr key={`${item.food_id || item.name}-${idx}`} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`food-row-${mc.id}-${idx}`}>
                      <td className="px-3 py-2">
                        <span className="font-medium text-slate-800">{getFoodName(item)}</span>
                        {desc && <p className="text-[9px] text-slate-400 leading-tight mt-0.5 max-w-[200px] truncate" title={desc}>{desc}</p>}
                      </td>
                      <td className="text-center px-1 py-1">
                        <Input value={item.weight} onChange={e => updateFoodWeight(mc.id, idx, e.target.value)}
                          className="h-7 text-xs text-center w-16 mx-auto" data-testid={`weight-${mc.id}-${idx}`} />
                      </td>
                      <td className="text-center text-slate-700 font-medium">{((item.calories_per100 || 0) * f).toFixed(0)}</td>
                      <td className="text-center text-blue-600">{((item.protein_per100 || 0) * f).toFixed(1)}g</td>
                      <td className="text-center text-amber-600">{((item.carbs_per100 || 0) * f).toFixed(1)}g</td>
                      <td className="text-center text-rose-600">{((item.fat_per100 || 0) * f).toFixed(1)}g</td>
                      <td className="text-center">
                        <button onClick={() => removeFoodFromMeal(mc.id, idx)} className="p-1 rounded hover:bg-red-50 text-red-400">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className={`${mc.bg} font-bold`}>
                  <td className={`px-3 py-2 ${mc.text}`}>TOTAL</td>
                  <td></td>
                  <td className="text-center text-slate-700">{totals.calories.toFixed(0)}</td>
                  <td className="text-center text-blue-700">{totals.protein.toFixed(1)}g</td>
                  <td className="text-center text-amber-700">{totals.carbs.toFixed(1)}g</td>
                  <td className="text-center text-rose-700">{totals.fat.toFixed(1)}g</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <p className={`text-center py-4 text-xs ${mc.text} opacity-60`}>{L.noFood} — {L.clickAdd} &ldquo;{L.addBtn}&rdquo;</p>
          )}

          {/* Recipe suggestions */}
          {items.length > 0 && (
            <div className="border-t border-slate-100">
              <button onClick={() => {
                if (!recipes[mc.id]) handleSuggestRecipes(mc.id);
                else setShowRecipes(prev => ({ ...prev, [mc.id]: !prev[mc.id] }));
              }}
                className="w-full flex items-center justify-between px-4 py-2 hover:bg-slate-50 text-xs"
                data-testid={`recipes-btn-${mc.id}`}>
                <span className="flex items-center gap-1.5 font-semibold text-slate-500">
                  <ChefHat className="w-3.5 h-3.5" /> {L.recipe}
                  {recipes[mc.id] && <span className="text-[9px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full">{recipes[mc.id].length}</span>}
                </span>
                {loadingRecipes[mc.id] && <RefreshCw className="w-3 h-3 text-slate-400 animate-spin" />}
              </button>

              {showRecipes[mc.id] && recipes[mc.id] && (
                <div className="px-4 pb-3 space-y-2" data-testid={`recipes-panel-${mc.id}`}>
                  {recipes[mc.id].map((recipe, rIdx) => (
                    <div key={rIdx} className="rounded-lg border border-slate-100 p-3 bg-gradient-to-br from-white to-slate-50" data-testid={`recipe-${mc.id}-${rIdx}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <h4 className="text-sm font-bold text-slate-800">{recipe.title}</h4>
                        <span className="flex items-center gap-1 text-[10px] text-slate-400"><Clock className="w-3 h-3" />{recipe.prep_time}</span>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed mb-2">{recipe.instructions}</p>
                      {recipe.tip && (
                        <div className="flex items-start gap-1.5 text-[10px] text-amber-700 bg-amber-50 rounded px-2 py-1.5">
                          <Lightbulb className="w-3 h-3 flex-shrink-0 mt-0.5" />
                          <span>{recipe.tip}</span>
                        </div>
                      )}
                    </div>
                  ))}
                  <button onClick={() => handleSuggestRecipes(mc.id)} disabled={loadingRecipes[mc.id]}
                    className="w-full text-center text-[10px] text-slate-400 hover:text-slate-600 py-1">
                    {loadingRecipes[mc.id] ? "Lade neue Rezepte..." : "Neue Rezepte generieren"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
